import { Component, computed, inject, input, SecurityContext } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";

import { Indicator } from "@components/shared/risk-assessment-templates/models";
import { TextConstants } from "@shared/constants";
import { ColorEnum } from "@shared/enums";

import {
  RiskAssessmentReportIndicatorMitigationStatus,
  RiskAssessmentReportIndicatorStatusEnum,
} from "../../constants";
import { RiskAssessmentReportIndicatorMitigation } from "../../models";

@Component({
  standalone: false,
  selector: "app-risk-assessment-indicator-report-print-view",
  templateUrl: "./risk-assessment-report-indicator-print-view.component.html",
  styleUrls: ["./risk-assessment-report-indicator-print-view.component.scss"],
})
export class RiskAssessmentReportIndicatorPrintViewComponent {
  readonly indicator = input.required<
    Indicator & { id: string; note: string; status: RiskAssessmentReportIndicatorStatusEnum }
  >();

  readonly mitigations = input<RiskAssessmentReportIndicatorMitigation[]>();

  readonly translations = {
    note: TextConstants.NOTE,
    guidance: $localize`:@@shared.guidance:Guidance`,
    mitigations: $localize`:@@shared.mitigationsparentheses:Mitigation(s)`,
  };

  readonly indicatorStatusOptions = [
    {
      value: RiskAssessmentReportIndicatorStatusEnum.RISK_ABSENT,
      label: $localize`:@@label.riskabsent:Risk absent`,
    },
    {
      value: RiskAssessmentReportIndicatorStatusEnum.RISK_PRESENT,
      label: $localize`:@@label.riskpresent:Risk present`,
    },
    {
      value: RiskAssessmentReportIndicatorStatusEnum.NOT_APPLICABLE,
      label: $localize`:@@label.notapplicable:Not applicable`,
    },
    { value: null, label: "" },
  ];

  private readonly sanitizer = inject(DomSanitizer);

  mapStatus = computed(() => ({
    [RiskAssessmentReportIndicatorStatusEnum.RISK_ABSENT]: {
      title: $localize`:@@label.riskabsent:Risk absent`,
      color: ColorEnum.GREEN,
    },
    [RiskAssessmentReportIndicatorStatusEnum.NOT_APPLICABLE]: {
      title: $localize`:@@label.notapplicable:Not applicable`,
      color: ColorEnum.GREY,
    },
    [RiskAssessmentReportIndicatorStatusEnum.RISK_PRESENT]: {
      title: this.isMitigated()
        ? TextConstants.RISK_PRESENT_MITIGATED
        : $localize`:@@label.riskpresent:Risk present`,
      color: this.isMitigated() ? ColorEnum.GREEN : ColorEnum.RED,
    },
  }));

  note = computed(() => this.getSafeHtml(this.indicator().note || "-"));

  guidance = computed(() => this.getSafeHtml(this.indicator().guidance || "-"));

  shouldDisplayMitigations = computed(
    () =>
      this.mitigations().length &&
      this.indicator().status === RiskAssessmentReportIndicatorStatusEnum.RISK_PRESENT,
  );

  isMitigated = computed(
    () =>
      this.mitigations().length &&
      this.mitigations().every(
        (m) =>
          m.status === RiskAssessmentReportIndicatorMitigationStatus.COMPLETED ||
          m.status === RiskAssessmentReportIndicatorMitigationStatus.NOT_APPLICABLE,
      ),
  );

  getSafeHtml(html: string) {
    return this.sanitizer.sanitize(SecurityContext.HTML, html);
  }
}
